/**
 * Execution Score — Phase 1b
 * ──────────────────────────
 * Canonical, locked formula (RECONCILIATION.md §3). Two engineers MUST
 * produce the same number. Decay is intentionally OFF during alpha.
 *
 * Formula:
 *   startingScore     = 100
 *   window            = last 10 completed jobs (per contractor)
 *   penalty (within window):
 *     high   → -30
 *     medium → -15
 *     low    → -5
 *   floor             = 0
 *   ceiling           = 100
 *   trial cap         = 85   (internal only — NEVER displayed contractor-side)
 *   sample-size guard = completedJobs < 3 → score = null
 *   attribution       = attributedTo === "owner" → EXCLUDED
 *   installerChange   = original installer keeps only pre-swap callbacks;
 *                       replacement installer inherits NONE
 *
 * Phase 2 (deferred — DO NOT implement now): decay weighting at ≥15 jobs.
 *
 * Anchor: mem://architecture/network-execution-phase-1a
 *         RECONCILIATION.md §11 (EN-2 resolver migration)
 *
 * EN-2 migration: score-subject membership now resolves through
 * `resolveExecutionResponsibilities()` rather than reading
 * `Job.assignedSubcontractorId` directly. Behavior is byte-identical
 * (resolver returns assignedSubcontractorId for every role in EN-2),
 * but the seam is now in place for future parent/crew routing.
 */

import type { Contractor, Job } from "@/data/types";
import type { Callback } from "./callback";
import { resolveExecutionResponsibilities } from "./resolveExecutionResponsibilities";

const STARTING_SCORE = 100;
const WINDOW_SIZE = 10;
const TRIAL_CAP = 85;
const MIN_SAMPLE = 3;

const PENALTY: Record<Callback["severity"], number> = {
  high: 30,
  medium: 15,
  low: 5,
};

export interface ExecutionScoreInput {
  contractor: Contractor;
  jobs: Job[];
  callbacks: Callback[];
  /** Override "now" for deterministic tests. */
  nowIso?: string;
}

export interface ExecutionScoreResult {
  /** null when sample < MIN_SAMPLE (insufficient history). */
  score: number | null;
  /** Number of completed jobs counted in the window. */
  completedInWindow: number;
  /** Total completed jobs (for sample-size gate). */
  completedTotal: number;
  /** Callbacks counted (post attribution + installerChange filter). */
  countedCallbacks: number;
  /** True when trial cap was applied. */
  trialCapped: boolean;
  /** Diagnostic — the per-callback deductions actually applied. */
  deductions: Array<{ callbackId: string; severity: Callback["severity"]; points: number }>;
}

/**
 * Resolve the set of completed jobs for this contractor, ordered most
 * recent first, capped at WINDOW_SIZE. We use the last activityHistory
 * timestamp as recency proxy (matches existing scorecard behavior).
 */
function isScoreSubjectFor(job: Job, contractor: Contractor): boolean {
  // EN-2: score-subject membership goes through the canonical resolver.
  // Legacy companyName fallback retained for pre-EN-1 demo records that
  // never got an `assignedSubcontractorId` written (display-name match
  // only — does not imply responsibility outside the resolver seam).
  const { scoreSubjectEntityId } = resolveExecutionResponsibilities(job);
  if (scoreSubjectEntityId === contractor.id) return true;
  if (
    !scoreSubjectEntityId &&
    job.assignedSubcontractor === contractor.companyName
  ) {
    return true;
  }
  return false;
}

function getRecentCompletedJobs(contractor: Contractor, jobs: Job[]): Job[] {
  const completed = jobs.filter(
    (j) => j.currentStage === "Completed" && isScoreSubjectFor(j, contractor),
  );

  const recencyOf = (j: Job): number => {
    const last = j.activityHistory?.[j.activityHistory.length - 1];
    if (!last?.date) return 0;
    return Date.parse(last.date);
  };

  return [...completed]
    .sort((a, b) => recencyOf(b) - recencyOf(a))
    .slice(0, WINDOW_SIZE);
}

/**
 * For an installerChange swap, the original installer only "owns"
 * callbacks filed BEFORE the swap timestamp; the replacement inherits
 * NOTHING from the prior period.
 */
function callbackBelongsToContractor(
  cb: Callback,
  contractor: Contractor,
  jobs: Job[],
): boolean {
  // Attribution gate — owner-attributed never counts against executor.
  if (cb.attributedTo === "owner") return false;

  // The installer snapshot at callback time IS the accountability anchor.
  if (cb.installerContractorIdAtCallback !== contractor.id) return false;

  // installerChange filter — verify this contractor was the installer at filedAt.
  const job = jobs.find((j) => j.id === cb.jobId);
  if (!job) return true; // missing job — keep simple, count it

  const swaps = job.installerChange ?? [];
  if (swaps.length === 0) return true;

  // Walk swaps chronologically; figure out who was installing at filedAt.
  const filedMs = Date.parse(cb.filedAt);
  const sorted = [...swaps].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

  // Reconstruct installer-at-time: start from earliest fromContractorId,
  // then transition on each swap.
  let installerAt: string | null = sorted[0].fromContractorId;
  for (const swap of sorted) {
    if (Date.parse(swap.at) <= filedMs) {
      installerAt = swap.toContractorId;
    } else {
      break;
    }
  }

  return installerAt === contractor.id;
}

export function computeExecutionScore(input: ExecutionScoreInput): ExecutionScoreResult {
  const { contractor, jobs, callbacks } = input;

  const recentJobs = getRecentCompletedJobs(contractor, jobs);
  const completedTotal = jobs.filter(
    (j) => j.currentStage === "Completed" && isScoreSubjectFor(j, contractor),
  ).length;

  // Sample-size guard.
  if (completedTotal < MIN_SAMPLE) {
    return {
      score: null,
      completedInWindow: recentJobs.length,
      completedTotal,
      countedCallbacks: 0,
      trialCapped: false,
      deductions: [],
    };
  }

  const recentJobIds = new Set(recentJobs.map((j) => j.id));

  const counted = callbacks.filter((cb) => {
    if (!recentJobIds.has(cb.jobId)) return false;
    return callbackBelongsToContractor(cb, contractor, jobs);
  });

  const deductions = counted.map((cb) => ({
    callbackId: cb.id,
    severity: cb.severity,
    points: PENALTY[cb.severity],
  }));

  const totalDeduction = deductions.reduce((s, d) => s + d.points, 0);
  let score = Math.max(0, Math.min(100, STARTING_SCORE - totalDeduction));

  // Trial cap — internal only, never surfaced contractor-side.
  let trialCapped = false;
  if (contractor.vettingStatus === "trial" && score > TRIAL_CAP) {
    score = TRIAL_CAP;
    trialCapped = true;
  }

  return {
    score,
    completedInWindow: recentJobs.length,
    completedTotal,
    countedCallbacks: counted.length,
    trialCapped,
    deductions,
  };
}
